import React, { useState, useEffect, useMemo } from 'react';
import styles from './AddQuantityModal.module.css';
import * as api from '../utils/api';
import { FiInfo } from 'react-icons/fi';

const AddQuantityModal = ({ isOpen, onClose, order, onAddQuantity, livePrice }) => {
  const [lots, setLots] = useState(1);
  const [availableMargin, setAvailableMargin] = useState(0);

  // Use the lotSize stored in the order object itself (Source of Truth)
  const lotSize = order?.lotSize || 1;
  const quantity = useMemo(() => lots * lotSize, [lots, lotSize]);

  useEffect(() => {
    if (order && lotSize > 1) {
      setLots(1);
    }
  }, [order, lotSize]);

  useEffect(() => {
    if (isOpen) {
      const fetchUser = async () => {
        try {
          const res = await api.getUser();
          setAvailableMargin(res.data.fund);
        } catch (err) {
          console.error(err);
        }
      };
      fetchUser();
    }
  }, [isOpen]);

  const requiredMargin = useMemo(() => {
    if (!livePrice || !quantity || !order?.margin) return 0;
    // leverage is stored in order.margin
    return (livePrice * quantity) / order.margin;
  }, [livePrice, quantity, order]);

  const insufficientFunds = requiredMargin > availableMargin;

  if (!isOpen || !order) return null;

  const handleLotsChange = (e) => {
    setLots(Number(e.target.value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddQuantity(order._id, quantity);
  };

  const isOption = lotSize > 1;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeaderControls}>
          <div>
            <h3>Add Quantity</h3>
            <p className={styles.symbolInfo}>
              <span>{order.tradeSymbol}</span>
              {livePrice && (
                <span className={styles.livePrice}>
                  ₹{livePrice.toFixed(2)}
                </span>
              )}
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>{isOption ? 'Lots' : 'Quantity'}</label>
              <div className={styles.quantityInput}>
                <input
                  type="number"
                  value={lots}
                  onChange={handleLotsChange}
                  min="1"
                />
                {isOption && (
                  <div className={styles.quantityDetails}>
                    <span className={styles.lotSizeInfo}>Lot Size: {lotSize}</span>
                    <span className={styles.totalQuantity}>Total Quantity: {quantity}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.marginInfo}>
            <div className={styles.marginItem}>
              <span>Required Margin (Approx)</span>
              <span>₹{requiredMargin.toFixed(2)}</span>
            </div>
            <div className={styles.marginItem}>
              <span>Available Balance</span>
              <span>₹{availableMargin.toFixed(2)}</span>
            </div>
          </div>

          {insufficientFunds && (
            <div className={styles.insufficientFundsWarning}>
              <FiInfo />
              <span>Insufficient Funds</span>
            </div>
          )}

          <div className={styles.buttonContainer}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.btnConfirm} disabled={insufficientFunds}>Add Quantity</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddQuantityModal;
